import { GenericAbortSignal } from 'axios';

import { Attr, AttrOptions } from 'models/flexible/Attr';
import { Paginated } from 'models/flexible/Paginated';
import type {
  AdminAttrCreateParams,
  AdminAttrIndexFields,
  AdminAttrIndexInclude,
  AdminAttrShowFields,
  AdminAttrShowInclude,
  AdminAttrUpdateParams,
} from 'services/api/fields';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const adminAttrsQueryKeys = {
  index: (params?: Record<string, any>) => ['adminAttrs', params] as const,
  show: (id: string) => ['adminAttrs', id] as const,
};

const URL = 'admin/attrs';

export type AdminAttrIndexParams = BaseIndexQueryParams & {
  client?: string;
  olioAttr?: string;
  active?: string;
  hasAttrValues?: boolean;
  distinctDisplayName?: boolean;
};

export async function adminIndexAttrs<I extends AdminAttrIndexInclude = never>(
  params: AdminAttrIndexParams,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Paginated<Attr<AdminAttrIndexFields<I>>>> {
  return http
    .get<
      Paginated<AttrOptions>
    >(URL, { params: { ...params, ...(includes?.length ? { include: includes.join(',') } : {}) }, signal })
    .then((res) => ({
      ...res.data,
      data: res.data.data.map((x) => new Attr(x)),
    })) as unknown as Promise<Paginated<Attr<AdminAttrIndexFields<I>>>>;
}

export async function adminShowAttr<I extends AdminAttrShowInclude = never>(
  id: string,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Attr<AdminAttrShowFields<I>>> {
  return http
    .get<AttrOptions>(`${URL}/${id}`, { params: { include: includes?.join(',') }, signal })
    .then((res) => new Attr(res.data)) as unknown as Promise<Attr<AdminAttrShowFields<I>>>;
}

export async function createAttr(params: AdminAttrCreateParams) {
  return http.post<AttrOptions>(URL, params).then((res) => new Attr(res.data));
}

export async function updateAttr(params: AdminAttrUpdateParams) {
  const { id, ...body } = params;
  return http.patch<AttrOptions>(`${URL}/${id}`, body).then((res) => new Attr(res.data));
}

export async function fetchAttrMatches(attrId: string, search: string, signal?: GenericAbortSignal) {
  return http
    .get<{ data: any[] }>(`${URL}/${attrId}/matches`, { params: { search }, signal })
    .then((res) => res.data.data);
}
